/**
 * Admin Email Management Page
 * Allows admins to view email history, statistics, and send manual emails
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Mail, Send, Filter, RefreshCw, Eye, TrendingUp, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import EmailFlowVisualizationPage from './admin/EmailFlowVisualizationPage';
import EmailFlowChangePage from './admin/EmailFlowChangePage';
import DOMPurify from 'dompurify';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { adminService } from '../services/firebase/admin';
import { EmailRecord, EmailStats, EmailRecipient } from '../types/admin';
// import { ROUTES } from '../utils/constants';

/**
 * Email Management Page Component
 */
export const AdminEmailManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'history' | 'compose' | 'stats' | 'flows' | 'changeFlow'>('history');
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Management</h1>
              <p className="text-gray-600">Manage email campaigns, view history, and monitor deliverability</p>
            </div>
            {/* Removed separate navigation for flows/change */}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center px-6 py-4 font-medium transition-colors ${
                activeTab === 'history'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Mail className="w-5 h-5 mr-2" />
              Email History
            </button>
            <button
              onClick={() => setActiveTab('compose')}
              className={`flex items-center px-6 py-4 font-medium transition-colors ${
                activeTab === 'compose'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Send className="w-5 h-5 mr-2" />
              Compose Email
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex items-center px-6 py-4 font-medium transition-colors ${
                activeTab === 'stats'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TrendingUp className="w-5 h-5 mr-2" />
              Statistics
            </button>
            <button
              onClick={() => setActiveTab('flows')}
              className={`flex items-center px-6 py-4 font-medium transition-colors ${
                activeTab === 'flows'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {/* Use a flow icon or fallback */}
              <span className="w-5 h-5 mr-2 inline-block"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M4 17V7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3Z"/></svg></span>
              User Flows
            </button>
            <button
              onClick={() => setActiveTab('changeFlow')}
              className={`flex items-center px-6 py-4 font-medium transition-colors ${
                activeTab === 'changeFlow'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {/* Use a settings icon or fallback */}
              <span className="w-5 h-5 mr-2 inline-block"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7Zm7.94-2.06a1 1 0 0 0 .21 1.09l.06.06a2 2 0 0 1 0 2.83l-1.42 1.42a2 2 0 0 1-2.83 0l-.06-.06a1 1 0 0 0-1.09-.21 1 1 0 0 0-.62.92V20a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-.09a1 1 0 0 0-.62-.92 1 1 0 0 0-1.09.21l-.06.06a2 2 0 0 1-2.83 0l-1.42-1.42a2 2 0 0 1 0-2.83l.06-.06a1 1 0 0 0 .21-1.09 1 1 0 0 0-.92-.62H4a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2h.09a1 1 0 0 0 .92-.62 1 1 0 0 0-.21-1.09l-.06-.06a2 2 0 0 1 0-2.83l1.42-1.42a2 2 0 0 1 2.83 0l.06.06a1 1 0 0 0 1.09.21H10V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v.09a1 1 0 0 0 .62.92 1 1 0 0 0 1.09-.21l.06-.06a2 2 0 0 1 2.83 0l1.42 1.42a2 2 0 0 1 0 2.83l-.06.06a1 1 0 0 0-.21 1.09V10h.09a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-.09a1 1 0 0 0-.92.62Z"/></svg></span>
              Change User Flow
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'history' && <EmailHistoryTab />}
        {activeTab === 'compose' && <ComposeEmailTab />}
        {activeTab === 'stats' && <EmailStatsTab />}
        {activeTab === 'flows' && (
          <div className="mt-6">
            <EmailFlowVisualizationPage />
          </div>
        )}
        {activeTab === 'changeFlow' && (
          <div className="mt-6">
            <EmailFlowChangePage />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Email History Tab
 */
const EmailHistoryTab: React.FC = () => {
  const [emails, setEmails] = useState<EmailRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [flowTypeFilter, setFlowTypeFilter] = useState<string>('');
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.getEmailHistory({
        status: statusFilter || undefined,
        limit: 100,
      });
      setEmails(result.emails);
    } catch (err) {
      console.error('Error loading emails:', err);
      setError('Failed to load email history');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const handleResend = async (emailId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setResendingId(emailId);
    setResendSuccess(null);
    setError(null);
    try {
      await adminService.resendEmail(emailId);
      setResendSuccess('Email queued for resending');
      setTimeout(() => setResendSuccess(null), 3000);
      loadEmails();
    } catch (err: any) {
      console.error('Error resending email:', err);
      setError(err.message || 'Failed to resend email');
    } finally {
      setResendingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'ERROR':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'PENDING':
      case 'PROCESSING':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'SUCCESS':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Success</span>;
      case 'ERROR':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Error</span>;
      case 'PENDING':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
      case 'PROCESSING':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Processing</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  return (
    <Card>
      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="SUCCESS">Success</option>
            <option value="PENDING">Pending</option>
            <option value="PROCESSING">Processing</option>
            <option value="ERROR">Error</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={flowTypeFilter}
            onChange={(e) => setFlowTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Flows</option>
            <option value="freeRegistration">Free Registration</option>
            <option value="paidMembership">Paid Membership</option>
            <option value="twelveMonthPackage">12-Month Package</option>
            <option value="stackcommerceLifetime">Lifetime (StackCommerce)</option>
            <option value="cancellation">Cancellation</option>
            <option value="leadMagnet">Lead Magnet</option>
            <option value="incompleteRegistration">Incomplete Registration</option>
            <option value="newSessions">New Sessions/Courses</option>
          </select>
        </div>
        <Button onClick={loadEmails} variant="secondary" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading emails...</span>
        </div>
      )}

      {/* Success State */}
      {resendSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800">{resendSuccess}</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Email List */}
      {!loading && !error && (
        <div className="space-y-3">
          {(() => {
            const filtered = emails.filter((email) => {
              if (!flowTypeFilter) return true;
              return (email.flowType || null) === flowTypeFilter;
            });
            if (filtered.length === 0) return (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No emails found</p>
              </div>
            );
            return filtered.map((email) => (
              <div
                key={email.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelectedEmail(email.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header with status icon and subject */}
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(email.status)}
                      <h3 className="font-medium text-gray-900">{email.subject}</h3>
                    </div>
                    
                    {/* Badges for email type and source */}
                    <div className="flex items-center gap-2 mb-3">
                      {email.isManual ? (
                        <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full flex items-center gap-1">
                          <span>✍️</span> Manual
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex items-center gap-1">
                          <span>🤖</span> Automated
                        </span>
                      )}
                      
                      {email.isScheduled && (
                        <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Scheduled
                        </span>
                      )}
                      
                      {email.isResend && (
                        <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" /> Resend
                        </span>
                      )}
                      
                      {email.resendHistory && email.resendHistory.length > 0 && (
                        <span className="px-2 py-1 text-xs font-medium bg-teal-100 text-teal-800 rounded-full flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" /> Resent {email.resendHistory.length}x
                        </span>
                      )}
                      
                      {email.templateId && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {email.templateId}
                        </span>
                      )}
                      
                      {email.flowType && (
                        <span className="px-2 py-1 text-xs font-medium bg-cyan-100 text-cyan-800 rounded-full">
                          Flow: {email.flowType} {email.flowStep ? `(Step ${email.flowStep})` : ''}
                        </span>
                      )}
                    </div>
                    
                    {/* Email metadata */}
                    <div className="text-sm text-gray-600 space-y-1.5">
                      <div className="flex items-start gap-2">
                        <span className="font-medium min-w-[80px]">Recipients:</span>
                        <span className="flex-1">
                          {Array.isArray(email.to) && email.to.length > 0 ? (
                            <>
                              {email.to.length === 1 ? (
                                email.to[0]
                              ) : (
                                <span className="font-medium text-blue-600">
                                  {email.to.length} recipients
                                </span>
                              )}
                            </>
                          ) : (
                            'Unknown'
                          )}
                        </span>
                      </div>
                      
                      {email.sentBy && (
                        <div className="flex items-start gap-2">
                          <span className="font-medium min-w-[80px]">Sent by:</span>
                          <span>{email.sentBy.name || email.sentBy.email || 'Unknown admin'}</span>
                        </div>
                      )}
                      
                      <div className="flex items-start gap-2">
                        <span className="font-medium min-w-[80px]">Created:</span>
                        <span>
                          {email.createdAt 
                            ? new Date(email.createdAt).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'Unknown'
                          }
                        </span>
                      </div>
                      
                      {email.sentAt && email.sentAt !== email.createdAt && (
                        <div className="flex items-start gap-2">
                          <span className="font-medium min-w-[80px]">Delivered:</span>
                          <span>
                            {new Date(email.sentAt).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                      
                      {email.attempts > 1 && (
                        <div className="flex items-start gap-2">
                          <span className="font-medium min-w-[80px]">Attempts:</span>
                          <span className="text-orange-600 font-medium">{email.attempts}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action buttons and status */}
                  <div className="flex items-center gap-3 ml-4">
                    {getStatusBadge(email.status)}
                    <button
                      onClick={(e) => handleResend(email.id, e)}
                      disabled={resendingId === email.id}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Resend email"
                    >
                      {resendingId === email.id ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                    <Eye className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
                
                {/* Error message if present */}
                {email.error && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                    <span className="font-medium">Error:</span> {email.error}
                  </div>
                )}
                
                {/* Show original email link for resends */}
                {email.isResend && email.originalEmailId && (
                  <div className="mt-2 text-xs text-gray-500">
                    Resent from email: {email.originalEmailId}
                  </div>
                )}
                
                {/* Show resend history for original emails */}
                {email.resendHistory && email.resendHistory.length > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">
                        This email has been resent {email.resendHistory.length} time{email.resendHistory.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {email.resendHistory.map((resend, idx) => (
                        <div key={idx} className="text-xs text-blue-700 flex items-center justify-between">
                          <span>
                            {new Date(resend.resentAt).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })} by {resend.resentBy}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEmail(resend.emailId);
                            }}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View resent email
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ));
          })()}
        </div>
      )}

      {/* Email Details Modal */}
      {selectedEmail && (
        <EmailDetailsModal
          emailId={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </Card>
  );
};

/**
 * Email Details Modal
 */
const EmailDetailsModal: React.FC<{ emailId: string; onClose: () => void }> = ({ emailId, onClose }) => {
  const [email, setEmail] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDetails = async () => {
      try {
        const details = await adminService.getEmailDetails(emailId);
        setEmail(details);
      } catch (err) {
        console.error('Error loading email details:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDetails();
  }, [emailId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Email Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ×
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : email ? (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Subject</h3>
                <p className="text-gray-900">{email.subject}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Recipients</h3>
                <p className="text-gray-900">{Array.isArray(email.to) ? email.to.join(', ') : email.to || 'Unknown'}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-2">HTML Body</h3>
                <div
                  className="border border-gray-200 rounded p-4 bg-gray-50 max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.htmlBody) }}
                />
              </div>
              {email.textBody && (
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Text Body</h3>
                  <pre className="border border-gray-200 rounded p-4 bg-gray-50 whitespace-pre-wrap text-sm">
                    {email.textBody}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-12">Email not found</p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Compose Email Tab
 */
const ComposeEmailTab: React.FC = () => {
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [textBody, setTextBody] = useState('');
  const [contentType, setContentType] = useState<'html' | 'rawHtml' | 'text'>('html');
  const [audienceType, setAudienceType] = useState<'selected' | 'all'>('selected');
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scheduleEmail, setScheduleEmail] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  const handleSend = async () => {
    if (!subject || !htmlBody) {
      setError('Subject and HTML content are required');
      return;
    }

    if (audienceType === 'selected' && selectedRecipients.length === 0) {
      setError('Please select at least one recipient or choose a broadcast audience');
      return;
    }

    if (scheduleEmail && (!scheduleDate || !scheduleTime)) {
      setError('Please select both date and time for scheduling');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      let scheduleAt = null;
      if (scheduleEmail) {
        scheduleAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
        
        // Validate future date
        if (new Date(scheduleAt) <= new Date()) {
          setError('Scheduled time must be in the future');
          setSending(false);
          return;
        }
      }

      const result = await adminService.sendManualEmail({
        subject,
        htmlBody,
        textBody: textBody || htmlBody.replace(/<[^>]*>/g, ''),
        sendToAll: audienceType !== 'selected',
        recipients: audienceType === 'selected' ? selectedRecipients : [],
        scheduleAt,
      });

      setSuccess(result.message);
      setSubject('');
      setHtmlBody('');
      setTextBody('');
      setSelectedRecipients([]);
      setAudienceType('selected');
      setScheduleDate('');
      setScheduleTime('');
      setScheduleEmail(false);
    } catch (err: any) {
      console.error('Error sending email:', err);
      setError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="text-xl font-bold text-gray-900 mb-6">Compose New Email</h2>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Subject */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter email subject"
          />
        </div>

        {/* Recipients */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipients
          </label>
          <div className="space-y-3">
            {/* Audience selector */}
            <div className="grid grid-cols-2 gap-2">
              {([
                { value: 'selected', label: 'Selected users', color: 'blue' },
                { value: 'all', label: 'All users', color: 'gray' },
              ] as const).map(({ value, label, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAudienceType(value)}
                  className={`px-3 py-2 text-sm rounded-lg border-2 font-medium transition-colors ${
                    audienceType === value
                      ? `border-${color}-500 bg-${color}-50 text-${color}-700`
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {audienceType === 'all' && (
              <div className="px-4 py-3 rounded-lg text-sm font-medium bg-gray-50 text-gray-700 border border-gray-200">
                Will send to all verified users.
              </div>
            )}
            {audienceType === 'selected' && (
              <RecipientSelector
                selectedRecipients={selectedRecipients}
                onSelectionChange={setSelectedRecipients}
              />
            )}
          </div>
        </div>

        {/* Content Editor with Tabs */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Email Content
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setContentType('html')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  contentType === 'html'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📝 Visual Editor
              </button>
              <button
                type="button"
                onClick={() => setContentType('rawHtml')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  contentType === 'rawHtml'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📄 Raw HTML
              </button>
              <button
                type="button"
                onClick={() => setContentType('text')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  contentType === 'text'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📃 Plain Text
              </button>
            </div>
          </div>
          
          {contentType === 'html' ? (
            <div>
              <RichTextEditor value={htmlBody} onChange={setHtmlBody} />
              <p className="text-xs text-gray-500 mt-2">
                💡 Use the visual editor to format your email with buttons, or switch to Raw HTML to paste HTML code.
              </p>
            </div>
          ) : contentType === 'rawHtml' ? (
            <div>
              <textarea
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                rows={16}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="Paste your HTML code here...\n\nExample:\n<div style='font-family: Arial, sans-serif;'>\n  <h1>Welcome!</h1>\n  <p>Your email content here...</p>\n</div>"
                spellCheck={false}
              />
              <div className="flex items-start gap-2 mt-2">
                <p className="text-xs text-gray-500 flex-1">
                  💡 Paste raw HTML code directly. Great for pre-designed templates or copying from external tools.
                </p>
                <button
                  type="button"
                  onClick={() => setContentType('html')}
                  className="text-xs text-blue-600 hover:text-blue-700 underline whitespace-nowrap"
                >
                  Switch to visual editor
                </button>
              </div>
            </div>
          ) : (
            <div>
              <textarea
                value={textBody}
                onChange={(e) => setTextBody(e.target.value)}
                rows={12}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="Enter plain text version of your email...\n\nThis is recommended for better deliverability and accessibility."
              />
              <p className="text-xs text-gray-500 mt-2">
                💡 Plain text version is recommended for better email deliverability. If left empty, it will be auto-generated from HTML.
              </p>
            </div>
          )}
        </div>

        {/* Schedule Options */}
        <div className="mb-6">
          <label className="flex items-center mb-3">
            <input
              type="checkbox"
              checked={scheduleEmail}
              onChange={(e) => setScheduleEmail(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">Schedule email for later</span>
          </label>
          
          {scheduleEmail && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time
                </label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {scheduleDate && scheduleTime && (
                <div className="col-span-2 text-sm text-gray-600">
                  📅 Scheduled for: {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            onClick={handleSend}
            disabled={sending}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            {sending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {scheduleEmail ? 'Scheduling...' : 'Sending...'}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {scheduleEmail ? 'Schedule Email' : 'Send Email'}
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

/**
 * Recipient Selector Component
 */
const RecipientSelector: React.FC<{
  selectedRecipients: string[];
  onSelectionChange: (recipients: string[]) => void;
}> = ({ selectedRecipients, onSelectionChange }) => {
  const [users, setUsers] = useState<EmailRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const result = await adminService.getEmailRecipients({
          searchQuery: searchQuery || undefined,
          subscriptionTier: tierFilter || undefined,
          limit: 100,
        } as any);
        setUsers(result.users);
      } catch (err) {
        console.error('Error loading users:', err);
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, [searchQuery, tierFilter]);

  const toggleRecipient = (userId: string) => {
    if (selectedRecipients.includes(userId)) {
      onSelectionChange(selectedRecipients.filter((id) => id !== userId));
    } else {
      onSelectionChange([...selectedRecipients, userId]);
    }
  };

  const selectAll = () => {
    onSelectionChange(users.map((u) => u.id));
  };

  const clearAll = () => {
    onSelectionChange([]);
  };

  return (
    <div className="border border-gray-300 rounded-lg p-4">
      {/* Search and Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search users..."
          className="flex-1 min-w-[140px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        />
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        >
          <option value="">All Tiers</option>
          <option value="free">Free</option>
          <option value="standard">Standard</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Bulk Actions */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={selectAll}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          Select All
        </button>
        <span className="text-gray-300">|</span>
        <button
          onClick={clearAll}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          Clear All
        </button>
        <span className="text-sm text-gray-600 ml-auto">
          {selectedRecipients.length} selected
        </span>
      </div>

      {/* User List */}
      <div className="max-h-64 overflow-y-auto space-y-2">
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
          </div>
        ) : users.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">No users found</p>
        ) : (
          users.map((user) => (
            <label
              key={user.id}
              className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedRecipients.includes(user.id)}
                onChange={() => toggleRecipient(user.id)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-3 flex-1 text-sm">
                <span className="font-medium text-gray-900">
                  {user.firstName} {user.lastName}
                </span>
                <span className="text-gray-600 ml-2">({user.email})</span>
                <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                  {user.subscriptionTier}
                </span>
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Simple Rich Text Editor Component
 */
const RichTextEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showHtml, setShowHtml] = useState(false);
  const isInitialMount = useRef(true);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Only set innerHTML on initial mount or when switching from HTML view
  useEffect(() => {
    if (editorRef.current && !showHtml) {
      if (isInitialMount.current) {
        editorRef.current.innerHTML = value;
        isInitialMount.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHtml]);

  // Update content when switching back from HTML view
  useEffect(() => {
    if (editorRef.current && !showHtml && value && editorRef.current.innerHTML === '') {
      editorRef.current.innerHTML = value;
    }
  }, [showHtml, value]);

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="px-3 py-1 text-sm font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="px-3 py-1 text-sm italic bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="px-3 py-1 text-sm underline bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Underline"
        >
          U
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h1>')}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Heading 1"
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h2>')}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<p>')}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Paragraph"
        >
          P
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => {
            const url = prompt('Enter URL:');
            if (url) execCommand('createLink', url);
          }}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="Insert Link"
        >
          Link
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => setShowHtml(!showHtml)}
          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
          title="View HTML"
        >
          {showHtml ? 'Visual' : 'HTML'}
        </button>
      </div>

      {/* Editor */}
      {showHtml ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full p-4 font-mono text-sm min-h-[400px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          className="w-full p-4 min-h-[400px] focus:outline-none focus:ring-2 focus:ring-blue-500 prose max-w-none"
        />
      )}
    </div>
  );
};

/**
 * Email Statistics Tab
 */
const EmailStatsTab: React.FC = () => {
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('last_30_days');

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getEmailStats(period);
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading statistics...</span>
        </div>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <p className="text-gray-500 text-center py-12">No statistics available</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <Card>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Email Statistics</h2>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
          </select>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Emails</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalEmails}</p>
            </div>
            <Mail className="w-12 h-12 text-blue-600 opacity-20" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Successful</p>
              <p className="text-3xl font-bold text-green-600">{stats.successCount}</p>
              <p className="text-xs text-gray-500">{stats.successRate}% success rate</p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pendingCount}</p>
            </div>
            <Clock className="w-12 h-12 text-yellow-600 opacity-20" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Failed</p>
              <p className="text-3xl font-bold text-red-600">{stats.errorCount}</p>
            </div>
            <XCircle className="w-12 h-12 text-red-600 opacity-20" />
          </div>
        </Card>
      </div>

      {/* Email Type Breakdown */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Type Breakdown</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-2">Manual Emails</p>
            <p className="text-2xl font-bold text-purple-600">{stats.manualCount}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Automated Emails</p>
            <p className="text-2xl font-bold text-blue-600">{stats.automatedCount}</p>
          </div>
        </div>
      </Card>

      {/* Flow Breakdown */}
      {stats.flowTypeBreakdown && Object.keys(stats.flowTypeBreakdown).length > 0 && (
        <Card>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Emails by Flow</h4>
          <div className="space-y-2">
            {Object.entries(stats.flowTypeBreakdown as Record<string, number>)
              .sort(([, a], [, b]) => b - a)
              .map(([flowType, count]) => {
                const pct = stats.totalEmails > 0 ? (count / stats.totalEmails * 100).toFixed(1) : 0;
                return (
                  <div key={flowType} className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full flex-shrink-0 bg-purple-500" />
                    <span className="text-sm text-gray-700 flex-1">{flowType}</span>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                    <div className="w-24 bg-gray-100 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-purple-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminEmailManagementPage;
